/**
 * `.dev.vars` — the local runtime env for `wrangler dev`, generated whole.
 *
 * The file is a materialization, never a source: platform-derived values
 * (JWTs, worker URLs) plus a cached copy of the app's secret store, rewritten
 * on every dev, test, and deploy run. Nothing reads it back,
 * and hand edits do not survive the next write — a value the app needs lives
 * in the store (`npx deepspace secrets set`), which is also the only thing a
 * deploy ships.
 *
 * It used to be three zones: an SDK section, a hand-edited section preserved
 * across writes, and the store cache — which required a divider grammar, a
 * dotenv parser to read the file back, and a deploy guard reconciling the two
 * sources (docs/proposals/secrets-source-of-truth.md). One source of truth
 * deleted all of it.
 */

import { effectivePlatformUrls } from '../env'
import { ApiError } from './api'
import { readAppId } from './app-identity'
import { appTargetMissingError } from './app-target'
import { fetchAppIdentityToken, fetchPublicKey, mintAppOwnerJwt } from './app-tokens'
import { Refusal } from './cli-errors'
import { writeSecretFileSync } from './secure-file'
import { devVarsPathFor } from './wrangler-env'

interface WriteDevVarsOptions {
  appId?: string
  /** Rendered store-secrets block (`renderSecretsCache`). */
  generatedSecretsCache?: string
}

const HEADER = [
  '# Generated by deepspace — do not edit; this file is rewritten on every',
  '# dev, test, and deploy run. App values live in the secret store:',
  '#   npx deepspace secrets set KEY=value',
].join('\n')

function requireAppIdFor(appDir: string, wranglerEnv?: string): string {
  const id = readAppId(appDir, wranglerEnv)
  if (id) return id
  // The same coded refusal (`app_not_initialized` / `no_app_id_for_env`) every
  // other command gives for this state — dev/test used to throw a bare Error.
  throw appTargetMissingError(appDir, wranglerEnv)
}

/** Write the whole file from platform truth + the store cache. */
export async function writeDevVars(
  appDir: string,
  ownerId: string,
  callerJwt: string,
  wranglerEnv?: string,
  opts: WriteDevVarsOptions = {},
): Promise<void> {
  // Override-aware, NOT the static preset: with DEEPSPACE_DEPLOY_URL (the
  // bench/self-hosted recipe) the identity-token mint must hit the SAME
  // service the app is registered on — the preset 404s there, and since the
  // 404 became a loud app_not_found refusal that misdiagnosis would hard-fail
  // dev/deploy and even hand agents an `app init` action against the wrong
  // platform. The .dev.vars URLs below inherit the overrides for the same
  // reason.
  const urls = effectivePlatformUrls()
  const publicKey = await fetchPublicKey(urls.auth)
  const appId = opts.appId ?? requireAppIdFor(appDir, wranglerEnv)
  let appOwnerJwt: string
  let appIdentityToken: string
  try {
    appOwnerJwt = await mintAppOwnerJwt(urls.auth, callerJwt, appId)
    appIdentityToken = await fetchAppIdentityToken(urls.deploy, callerJwt, appId)
  } catch (err: unknown) {
    // CLI-001, raised at the ONE place the unresolvable-id ApiError
    // originates so every caller (dev, test, deploy) hands out
    // the same executable remedy. First hop only: `app init` verifies with
    // the server and, for an unregistered EXISTING id, refuses again with
    // the --new-id fork — that second, destructive hop stays a human
    // decision.
    if (err instanceof ApiError && err.code === 'app_not_found') {
      throw new Refusal(err.message, 'app_not_found', {
        actionRequired: true,
        action: {
          cwd: appDir,
          // Env-scoped like every upstream step: a bare `app init` reads and
          // writes the TOP-LEVEL [vars] slot — under --env it would either
          // no-op on the prod id or mint a new app into the wrong scope.
          argv: ['deepspace', 'app', 'init', ...(wranglerEnv ? ['--env', wranglerEnv] : [])],
        },
      })
    }
    throw err
  }

  // Wrangler requires quotes around multi-line PEM values. ALLOW_DEBUG_ROUTES
  // is dev-only here: deploys ship the store, so this value never reaches
  // production — a store entry for the same key lands later in the file and
  // wins locally too (dotenv last-write).
  const sdkVars = [
    `AUTH_JWT_PUBLIC_KEY="${publicKey}"`,
    `AUTH_JWT_ISSUER=${urls.auth}/api/auth`,
    `AUTH_WORKER_URL=${urls.auth}`,
    `API_WORKER_URL=${urls.api}`,
    `PLATFORM_WORKER_URL=${urls.platform}`,
    `OWNER_USER_ID=${ownerId}`,
    `APP_OWNER_JWT=${appOwnerJwt}`,
    `APP_IDENTITY_TOKEN=${appIdentityToken}`,
    `ALLOW_DEBUG_ROUTES=true`,
  ].join('\n')

  const devVarsPath = devVarsPathFor(appDir)
  const cache = opts.generatedSecretsCache
  writeSecretFileSync(
    devVarsPath,
    [HEADER, '', sdkVars, ...(cache ? ['', cache] : []), ''].join('\n'),
  )
}
